import React, { useState } from 'react';
import { startAuthentication, browserSupportsWebAuthn, browserSupportsWebAuthnAutofill, WebAuthnAbortService } from '@simplewebauthn/browser';
import { api } from '../api';
import { Icon } from './brand';
import { useFirmIdentity } from './ComplianceContent';
import { deviceHasPasskey, markDeviceHasPasskey, clearDeviceHasPasskey, setPasskeySetupIntent } from '../utils/passkeyDevice';
import { useInstallPrompt, isStandalone } from '../hooks/useInstallPrompt';

interface Props { onLogin: (user: any, loginResponse?: any) => void; }

// Same "add to home screen" affordance as the post-login InstallPrompt
// (components/mobile/InstallPrompt.tsx), offered before credentials so a
// client never has to find it buried in the app later. See
// hooks/useInstallPrompt.ts for the shared beforeinstallprompt-capture /
// iOS-detection logic — this is deliberately NOT dismissible/localStorage-
// gated like the post-login one, since it's low-frequency (only seen while
// logged out) and a client who ignores it once may still want it next time.
function AddToHomeScreenNudge() {
  const { canInstall, promptInstall, isIOS: showIOS } = useInstallPrompt();
  if (isStandalone()) return null;

  const showAndroid = !showIOS && canInstall;
  if (!showIOS && !showAndroid) return null;

  return (
    <div style={{
      marginTop: '16px', padding: '10px 14px', borderRadius: '4px',
      background: '#F9FAFB', border: '1px solid #F3F4F6',
      display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <Icon name="upload" size={15} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: '#374151' }}>
          {showIOS ? 'Add to your home screen' : 'Install this app'}
        </p>
        <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#9CA3AF' }}>
          {showIOS
            ? 'Tap the Share icon, then "Add to Home Screen".'
            : 'One-tap access with no browser bars.'}
        </p>
      </div>
      {showAndroid && (
        <button
          onClick={() => promptInstall()}
          style={{
            background: 'var(--brand-primary)', color: 'white', border: 'none',
            borderRadius: '4px', padding: '6px 12px', cursor: 'pointer',
            fontFamily: 'inherit', fontWeight: 700, fontSize: '12px', flexShrink: 0,
          }}
        >
          Install
        </button>
      )}
    </div>
  );
}

// Firm-driven login mark: the firm's uploaded logo, or a monogram of its name.
// Never a hardcoded firm logo.
export function FirmLogo({ size = 44 }: { size?: number } = {}) {
  const firm = useFirmIdentity();
  if (firm.logo) {
    return (
      <img
        src={firm.logo}
        alt={firm.short_name}
        style={{ width: size, height: size, borderRadius: "4px", objectFit: "cover" }}
        onError={e => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
      />
    );
  }
  return (
    <div aria-hidden style={{
      width: size, height: size, borderRadius: "4px",
      background: "var(--brand-primary)", color: "white",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: Math.round(size * 0.45), fontWeight: 800,
    }}>
      {(firm.short_name || "•").trim().charAt(0).toUpperCase()}
    </div>
  );
}

export default function Login({ onLogin }: Props) {
  const FIRM = useFirmIdentity();
  // useFirmIdentity seeds from FIRM_INFO, whose short_name is the placeholder
  // string "Your Firm", so a truthy check isn't enough to know it resolved.
  const firmResolved = !!FIRM.short_name && FIRM.short_name !== 'Your Firm';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Passwordless passkey (open → Face ID → in) state.
  const [pkSupported] = useState(() => { try { return browserSupportsWebAuthn(); } catch { return false; } });
  const [pkBusy, setPkBusy] = useState(false);
  // Non-error informational note (blue), e.g. "no passkey here yet — sign in and
  // we'll set it up". Distinct from `error` so it doesn't read as a failure.
  const [notice, setNotice] = useState('');

  // MFA flow state
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  // Multi-factor: the selected factor + all the user's enrolled factors (so we
  // can offer "Use a different option") + the masked SMS destination + resend.
  const [mfaMethod, setMfaMethod] = useState<'sms' | 'totp' | 'recovery' | 'passkey' | 'email'>('totp');
  const [mfaMethods, setMfaMethods] = useState<string[]>([]);
  const [showOtherOptions, setShowOtherOptions] = useState(false);
  // Whether the server allows the zero-setup "Email me a code" fallback for
  // this sign-in (never for staff, paused 72h after a password reset). An
  // explicitly enrolled email factor arrives in mfaMethods instead.
  const [emailFallback, setEmailFallback] = useState(true);
  const [phoneLast4, setPhoneLast4] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const isSms = mfaMethod === 'sms';
  const isRecovery = mfaMethod === 'recovery';
  const isPasskey = mfaMethod === 'passkey';
  const isEmail = mfaMethod === 'email';
  // The single alternate promoted to a button under the verify action (see the
  // render). A synced passkey this browser hasn't flagged yet is the one-tap
  // win, so it takes precedence; otherwise the zero-setup email fallback
  // (mfa.py's mfa_email_send) when the server says it's allowed.
  const emailOffered = emailFallback || mfaMethods.includes('email');
  const contextualAlt: 'passkey' | 'email' | null =
    (pkSupported && mfaMethods.includes('passkey') && !isPasskey) ? 'passkey'
      : (!isEmail && emailOffered) ? 'email'
        : null;

  // Everything else lives in "Verify another way". 'email' joins the pool when
  // the fallback is allowed, and whichever option was promoted to its own
  // button is filtered out so it isn't offered twice.
  const otherOptions = Array.from(new Set([...mfaMethods, ...(emailOffered ? ['email'] : [])]))
    .filter(m => m !== mfaMethod && m !== contextualAlt);

  const METHOD_LABEL: Record<string, string> = {
    passkey: 'Passkey',
    sms: 'Text message code',
    totp: 'Authenticator app',
    recovery: 'Recovery code',
    email: 'Email code',
  };

  // Forgot-password flow state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const handleLogin = async () => {
    if (!email || !password) { setError('Please enter your email and password.'); return; }
    setLoading(true);
    setError('');
    try {
      const data = await api.auth.login(email, password);
      if (data.needs_mfa) {
        // Available factors (preferred first); fall back to the single method
        // for older servers that don't send the list.
        const methods: string[] = Array.isArray(data.mfa_methods) && data.mfa_methods.length
          ? data.mfa_methods
          : [data.mfa_method === 'sms' ? 'sms' : 'totp'];
        // Older servers don't send the flag; treat that as allowed.
        const fallbackOk = data.email_fallback !== false;
        let pref = (methods.includes(data.mfa_method) ? data.mfa_method : methods[0]) as 'sms' | 'totp' | 'recovery' | 'passkey' | 'email';
        // Nothing real enrolled makes the server prefer the email fallback; if
        // that's refused right now, a recovery code is the way in.
        if (pref === 'email' && !fallbackOk && !methods.includes('email')) {
          pref = (methods.find(m => m !== 'email') || 'recovery') as typeof pref;
        }
        // Don't DEFAULT to the passkey screen on a device with no local passkey —
        // the credential may live on another device, so this one can't auto-fire and
        // the user would just see "Waiting for passkey…" and have to pick another
        // option. Land on the first usable factor (TOTP/SMS) IF the account
        // actually has one. If passkey is the account's ONLY enrolled factor,
        // there is no other real option to fall back to — `methods.find` used
        // to fall through to a hardcoded 'totp', showing a 6-digit code box the
        // user has no authenticator to fill in (a dead end that stranded a
        // real client for days). Stay on 'passkey' instead: it's the only
        // action that can actually succeed here, via the browser's own
        // cross-device flow (scan a QR with the device that has the passkey).
        if (pref === 'passkey' && !deviceHasPasskey()) {
          const fallback = methods.find(m => m !== 'passkey' && m !== 'recovery');
          if (fallback) pref = fallback as 'sms' | 'totp' | 'recovery' | 'passkey' | 'email';
        }
        setMfaMethods(methods);
        setEmailFallback(fallbackOk);
        setMfaMethod(pref);
        setMfaToken(data.mfa_token);
        setLoading(false);
        // SMS/email send the code; passkey fires the OS prompt — both so the
        // next screen is immediately actionable. But only auto-open the
        // passkey prompt when THIS device has a local passkey — otherwise the
        // browser jumps straight to a cross-device QR, which is jarring when
        // the user just wants to type their authenticator code. They can
        // still tap "Use passkey" or pick another option.
        if (pref === 'sms') sendLoginSms(data.mfa_token);
        else if (pref === 'email') sendLoginEmailCode(data.mfa_token);
        else if (pref === 'passkey' && deviceHasPasskey()) startPasskey(data.mfa_token);
        return;
      }
      onLogin(data.user, data);
    } catch (e: any) {
      // Map common auth errors to friendlier copy. Falls back to the
      // raw detail if it's something we haven't anticipated.
      const status = e.status;
      const detail = (e.message || "").toLowerCase();
      if (status === 401 || detail.includes("invalid") || detail.includes("credential")) {
        setError("Email or password didn't match. Try again or reset your password.");
      } else if (status === 429 || detail.includes("too many")) {
        setError("Too many attempts. Try again in a few minutes.");
      } else if (status === 403 || detail.includes("locked") || detail.includes("disabled")) {
        setError("This account is locked. Contact your advisor to unlock it.");
      } else if (detail.includes("network") || detail.includes("fetch")) {
        setError("Couldn't reach the server. Check your connection and try again.");
      } else {
        setError(e.message || "Something didn't work. Try again or contact support.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit when 6 digits typed (skipped for recovery codes since
  // those vary in length). Debounced 800ms so a late "Remember this
  // device" checkbox click cancels and re-arms the timer with the new
  // trustDevice state — otherwise typing the code and clicking the box
  // race each other and the request fires with stale trustDevice=false.
  // trustDevice is in the dep array deliberately for the same reason.
  React.useEffect(() => {
    const cleaned = mfaCode.replace(/\s/g, "");
    if (mfaToken && mfaMethod !== 'recovery' && cleaned.length === 6 && /^\d+$/.test(cleaned) && !loading) {
      const t = setTimeout(() => { handleMfaVerify(); }, 800);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfaCode, mfaToken, mfaMethod, trustDevice]);

  // Switch to a different enrolled factor mid-login.
  const selectMethod = (m: 'sms' | 'totp' | 'recovery' | 'passkey' | 'email') => {
    setMfaMethod(m);
    setMfaCode('');
    setError('');
    setShowOtherOptions(false);
    if (m === 'sms' && mfaToken) sendLoginSms(mfaToken);
    else if (m === 'email' && mfaToken) sendLoginEmailCode(mfaToken);
    else if (m === 'passkey' && mfaToken) startPasskey(mfaToken);
  };

  // Passkey assertion: fetch options, run the WebAuthn ceremony (OS biometric
  // prompt), verify -> session. The token can come from the just-issued login
  // response (auto-trigger) or current state (chooser).
  const startPasskey = async (token?: string) => {
    const t = token || mfaToken;
    if (!t) return;
    setLoading(true);
    setError('');
    // Cross-device (QR/hybrid) ceremonies can hang forever if the phone's
    // side of the tunnel never completes — Bluetooth off on either device,
    // Android's Location-Services-gates-BLE-scan quirk, no route to the
    // relay, a stalled camera scan. The browser doesn't time this out on its
    // own (a real client watched it spin indefinitely after scanning), so we
    // force it after longer than any real scan+approve should take.
    // WebAuthnAbortService.cancelCeremony() aborts whatever startAuthentication
    // is currently waiting on (the library wires this up automatically).
    let timedOut = false;
    const timer = setTimeout(() => { timedOut = true; WebAuthnAbortService.cancelCeremony(); }, 45_000);
    try {
      const optionsJSON = await api.auth.passkeyAuthOptions(t);
      const cred = await startAuthentication({ optionsJSON });
      const data = await api.auth.passkeyAuthVerify(t, cred, trustDevice);
      markDeviceHasPasskey();
      onLogin(data.user, data);
    } catch (e: any) {
      // NotAllowedError = user dismissed/timed out the OS prompt (includes a
      // cross-device QR ceremony that never completed — no phone handy, no
      // camera, Bluetooth off, etc). "Or pick a different option" used to be
      // a lie for a passkey-only account (the chooser it points at only
      // rendered when mfaMethods.length > 1) — "Email code" is now always in
      // that list regardless of enrollment (see otherOptions / mfa_email_send),
      // so the phrase is always true and the advisor-reset copy is no longer
      // the only recourse.
      if (timedOut) {
        setError("That's taking too long. Check that Bluetooth is on for both devices and your phone has a signal, then try again.");
      } else if (e?.name === 'NotAllowedError' || e?.name === 'AbortError') {
        setError('Passkey prompt was dismissed. Tap "Use passkey" to try again, or pick a different option.');
      } else {
        setError(e?.message || 'Passkey sign-in failed. Try a different option.');
      }
      setLoading(false);
    } finally {
      clearTimeout(timer);
    }
  };

  // Passwordless login: usernameless passkey assertion, no email/password. Used
  // by the "Sign in with Face ID" button (modal), the auto-prompt on open (modal,
  // silent), and browser autofill (conditional). On success the assertion alone
  // mints a full session.
  const startPasswordlessLogin = async (opts?: { conditional?: boolean; silent?: boolean }) => {
    if (!pkSupported) return;
    const conditional = !!opts?.conditional;
    const silent = !!opts?.silent;
    // Explicit "Sign in with passkey" tap on a device that has no local passkey:
    // firing the OS prompt here shows a confusing cross-device/Apple screen for a
    // credential that isn't on this device. Skip it — offer setup-on-next-login
    // instead. (The synced/cross-device case is still covered by the silent
    // conditional-autofill path that runs on open.)
    if (!conditional && !silent && !deviceHasPasskey()) {
      setPasskeySetupIntent();
      setNotice("No passkey on this device yet. Sign in with your password below and we'll set one up for next time.");
      return;
    }
    if (!conditional) { setPkBusy(true); setError(''); setNotice(''); }
    // Same hang risk as startPasskey: the local "has passkey" flag can be
    // stale (cleared cookies/storage, a synced profile that diverged), so
    // the browser falls through to a cross-device QR that never completes.
    // Force it after longer than any real local prompt or scan+approve
    // should take. Skipped for the silent conditional-autofill listener —
    // that one is inert until the user acts, never a blocking ceremony.
    let timedOut = false;
    const timer = conditional ? null : setTimeout(() => { timedOut = true; WebAuthnAbortService.cancelCeremony(); }, 45_000);
    try {
      const optionsJSON = await api.auth.passkeyLoginOptions();
      const cred = await startAuthentication({ optionsJSON, useBrowserAutofill: conditional });
      const data = await api.auth.passkeyLoginVerify(cred, trustDevice);
      markDeviceHasPasskey();
      pkFailCount.current = 0;
      onLogin(data.user, data);
    } catch (e: any) {
      // Server no longer recognizes this device's passkey (removed elsewhere) —
      // drop the local flag so we stop auto-prompting a dead credential.
      if (e?.status === 401) clearDeviceHasPasskey();
      if (conditional) return;   // autofill path: never intrudes on failure
      setPkBusy(false);
      if (timedOut) {
        setNotice("That's taking too long. Check that Bluetooth is on for both devices, or use your password below.");
        return;
      }
      const dismissed = e?.name === 'NotAllowedError' || e?.name === 'AbortError';
      // A device-bound passkey that's since been lost (new device, wiped keychain,
      // rebound credential) makes the OS prompt flash and die with NotAllowedError
      // while the local "has passkey" flag stays set — so we keep auto-firing a
      // dead prompt and the user is stranded with no guidance (this locked out a
      // real client). Count the miss; after two, stop trusting the flag so the
      // next visit drops straight to the password form.
      if (dismissed && deviceHasPasskey()) {
        pkFailCount.current += 1;
        if (pkFailCount.current >= 2) clearDeviceHasPasskey();
      }
      if (opts?.silent) return;   // on-open auto-prompt must never nag on failure
      if (dismissed && !deviceHasPasskey()) {
        // They tapped "Face ID" but nothing is enrolled on this device. Queue
        // the intent: sign in with a password now, then we drop them straight
        // into passkey setup so next time the button just works.
        setPasskeySetupIntent();
        setNotice("No passkey on this device yet. Sign in with your password below and we'll set one up for next time.");
      } else if (dismissed) {
        // Prompt dismissed, or this device's known passkey didn't complete.
        // Never leave a blank screen — point at the always-present password form.
        setNotice("Couldn't finish signing in with your passkey — use your password below, or try the passkey again.");
      } else {
        // A real failure (not a user cancel). Surface it.
        setError(e?.message || 'Passkey sign-in failed. Use your password instead.');
      }
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

  // On open: returning users on a device that already has a passkey go straight
  // to Face ID (modal). Everyone else gets the non-intrusive autofill path if
  // the browser supports it (covers e.g. a synced iCloud passkey on a new
  // device). Fires exactly once; both paths fail silently to the password form.
  // Does THIS device have a passkey for this account? Drives which of the two
  // sign-in actions carries primary weight (see the button pair below). Read
  // once per mount rather than on every render: the value only changes as a
  // result of an action that already re-navigates.
  const [pkPrimary] = useState(() => {
    try { return browserSupportsWebAuthn() && deviceHasPasskey(); } catch { return false; }
  });

  const pkAutoFired = React.useRef(false);
  // Consecutive passwordless-passkey failures on a device we thought had one.
  // Two misses → we stop trusting the local flag (see startPasswordlessLogin).
  const pkFailCount = React.useRef(0);
  React.useEffect(() => {
    if (!pkSupported || pkAutoFired.current || mfaToken || showForgot) return;
    pkAutoFired.current = true;
    if (deviceHasPasskey()) {
      startPasswordlessLogin({ silent: true });
    } else {
      (async () => {
        try {
          if (await browserSupportsWebAuthnAutofill()) startPasswordlessLogin({ conditional: true, silent: true });
        } catch { /* no autofill support — password form stands */ }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Text (or re-text) the SMS-MFA code. Starts a 30s resend cooldown.
  const sendLoginSms = async (token: string) => {
    try {
      const res = await api.auth.mfaSmsSend(token);
      setPhoneLast4(res.phone_last4 || null);
      setResendIn(30);
    } catch (e: any) {
      setError(e.message || "We couldn't text your code. Contact your advisor for help signing in.");
    }
  };

  // Email (or re-email) the universal-fallback MFA code. Same shape as
  // sendLoginSms — reuses the same resend cooldown state since only one of
  // sms/email is ever active on this screen at a time.
  const sendLoginEmailCode = async (token: string) => {
    try {
      await api.auth.mfaEmailSend(token);
      setResendIn(30);
    } catch (e: any) {
      setError(e.message || "We couldn't email your code. Contact your advisor for help signing in.");
    }
  };

  // Resend cooldown ticker.
  React.useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn(n => (n > 0 ? n - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const handleMfaVerify = async () => {
    const code = mfaCode.trim();
    if (!code) {
      setError(isSms
        ? 'Enter the 6-digit code we texted you.'
        : isEmail ? 'Enter the 6-digit code we emailed you.'
        : isRecovery ? 'Enter a recovery code from your backup list.' : 'Enter the 6-digit code from your authenticator app.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Recovery codes intentionally skip the trust-device option — those are
      // emergency bypasses and shouldn't leave a 30-day MFA bypass cookie
      // behind. Backend enforces this server-side too. `method` tells the
      // server which factor this code is for.
      const data = await api.auth.mfaVerify(mfaToken!, code, trustDevice && !isRecovery, mfaMethod);
      onLogin(data.user, data);
    } catch (e: any) {
      const detail = (e.message || "").toLowerCase();
      if (detail.includes("invalid") || detail.includes("expired")) {
        setError(isSms || isEmail
          ? "That code didn't match or expired. Tap Resend to get a new one."
          : isRecovery
            ? "That recovery code didn't match. Try another from your backup list."
            : "That code didn't match. Make sure your authenticator app's clock is in sync and try again.");
      } else if (e.status === 429) {
        setError("Too many tries. Wait a moment, then try again.");
      } else {
        setError(e.message || "Couldn't verify the code. Try again.");
      }
      setLoading(false);
    }
  };

  const handleCancelMfa = () => {
    setMfaToken(null);
    setMfaCode('');
    setError('');
    setMfaMethod('totp');
    setMfaMethods([]);
    setShowOtherOptions(false);
    setPhoneLast4(null);
    setResendIn(0);
  };

  const handleForgotSubmit = async () => {
    setForgotError('');
    if (!forgotEmail) { setForgotError('Enter the email on your account'); return; }
    setForgotSubmitting(true);
    try {
      await api.auth.requestPasswordReset(forgotEmail);
      setForgotSent(true);
    } catch (e: any) {
      setForgotError(e.message || 'Something went wrong');
    } finally {
      setForgotSubmitting(false);
    }
  };

  const closeForgot = () => {
    setShowForgot(false);
    setForgotEmail('');
    setForgotSent(false);
    setForgotError('');
  };

  const inp: React.CSSProperties = {
    width: '100%', padding: '11px 14px', borderRadius: '4px',
    border: '1.5px solid #E5E7EB', background: 'white', color: '#111827',
    fontSize: '14px', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  };

  return (
    <div className="mp-login" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#F9FAFB', fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        input:focus { border-color: var(--brand-primary) !important; box-shadow: 0 0 0 3px var(--brand-accent-10); }
        /* iOS Safari zooms the whole page when a focused input is under 16px,
           and every field here inherits the shared inp style at 14px, so
           tapping Email on an
           iPhone shoved the card sideways and the user had to pinch back out
           mid-login. Inline styles outrank a stylesheet, hence !important;
           scoped to .mp-login so it can't reach the rest of the app. */
        @media (max-width: 640px) {
          .mp-login input[type="email"],
          .mp-login input[type="text"],
          .mp-login input[type="password"] { font-size: 16px !important; }
        }
      `}</style>

      {/* Background decoration */}
      <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', top: '-120px', right: '-120px', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, var(--brand-accent-06) 0%, transparent 70%)' }} />
        <div style={{ position: 'absolute', bottom: '-80px', left: '-80px', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, var(--brand-accent-04) 0%, transparent 70%)' }} />
      </div>

      <div style={{ width: '100%', maxWidth: '400px', padding: '0 24px', animation: 'fadeUp 0.4s ease' }}>

        {/* Sourdough owns the header. The firm is named inside the card
            instead (the subhead under the heading): enough that a client
            entering credentials sees whose account this is, without handing
            over the platform's identity to it. */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <img src={(process.env.PUBLIC_URL || '') + '/sourdough-logo.png'} alt="Sourdough" style={{ height: '38px', width: 'auto', display: 'block' }} />
            <span style={{ fontSize: '24px', fontWeight: 700, color: '#111827', letterSpacing: '-0.3px' }}>Sourdough</span>
          </div>
          <p style={{ margin: 0, color: '#9CA3AF', fontSize: '14px' }}>Align your money with your lifestyle</p>
        </div>

        {/* Card */}
        <div style={{ background: 'white', borderRadius: '4px', padding: '32px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid #F3F4F6' }}>
          <h2 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: 700, color: '#111827' }}>Sign in to your account</h2>
          {/* The firm named at the point of credential entry. Rendered only
              once /firm-settings/public has resolved — useFirmIdentity seeds
              short_name with the literal placeholder "Your Firm", and "Your
              plan with Your Firm" is worse than a beat of nothing. The
              non-breaking space holds the line's height so the card doesn't
              reflow when it lands. */}
          <p style={{ margin: '0 0 24px', fontSize: '13px', color: '#6B7280' }}>
            {firmResolved ? `Your plan with ${FIRM.short_name}` : '\u00A0'}
          </p>

          {error && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '4px', padding: '12px 14px', marginBottom: '20px', color: '#DC2626', fontSize: '13px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Icon name="alert" size={16} /> {error}
            </div>
          )}

          {notice && !error && (
            <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '4px', padding: '12px 14px', marginBottom: '20px', color: '#1D4ED8', fontSize: '13px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <Icon name="lock" size={16} /> {notice}
            </div>
          )}

          {!mfaToken && !showForgot ? (
            <>
              {/* Passwordless: one tap → Face ID / fingerprint → in. Only shown
                  where the browser supports passkeys; the password form stays
                  right below for anyone without one set up. */}
              {pkSupported && (
                <>
                  {/* Exactly ONE filled primary on this screen. Which one it is
                      depends on what this device can actually do: a device with
                      a passkey gets the one-tap path as the primary and drops
                      the password submit to outline; a device without one keeps
                      the password as primary and leaves this as a secondary
                      offer. (It is never a dead end either way — startPasswordless-
                      Login bails early with a helpful notice when the device has
                      no credential, rather than firing an empty OS dialog.) */}
                  <button
                    onClick={() => startPasswordlessLogin()} disabled={pkBusy || loading}
                    style={pkPrimary
                      ? { width: '100%', padding: '13px', background: pkBusy ? '#F3F4F6' : 'var(--brand-primary)', color: pkBusy ? '#9CA3AF' : 'white', border: 'none', borderRadius: '4px', fontSize: '14px', fontWeight: 700, cursor: pkBusy ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '14px' }
                      : { width: '100%', padding: '12px', background: 'white', color: '#374151', border: '1.5px solid #E5E7EB', borderRadius: '4px', fontSize: '13px', fontWeight: 700, cursor: pkBusy ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '14px' }}
                  >
                    {pkBusy ? (
                      <>
                        <div style={{ width: '16px', height: '16px', border: '2px solid #D1D5DB', borderTopColor: '#9CA3AF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        Waiting for passkey…
                      </>
                    ) : (
                      <>
                        <Icon name="lock" size={16} />
                        Sign in with a passkey
                      </>
                    )}
                  </button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 18px' }}>
                    <div style={{ flex: 1, height: '1px', background: '#F3F4F6' }} />
                    <span style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600 }}>or use your password</span>
                    <div style={{ flex: 1, height: '1px', background: '#F3F4F6' }} />
                  </div>
                </>
              )}

              {/* A real <form onSubmit> — not just onClick + onKeyDown — is what
                  makes Chrome/Safari/Firefox and mobile keyboards reliably offer
                  "Save password" after a successful sign-in. name+autoComplete on
                  their own aren't enough; the browser's credential-save heuristic
                  keys off the form submit event. */}
              <form onSubmit={e => { e.preventDefault(); if (!loading) handleLogin(); }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>Email</label>
                  <input
                    type="email" name="username" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com" style={inp}
                    autoComplete="username webauthn"
                  />
                </div>

                {/* "Forgot password?" rides the label row rather than sitting in
                    its own row between the last field and the submit button, so
                    the primary action follows the final input directly. Show/hide
                    is an in-field toggle for the same reason: it was costing a
                    full row and reading as a form question rather than a control. */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Password</label>
                    <button
                      type="button"
                      onClick={() => { setShowForgot(true); setForgotEmail(email); setError(''); }}
                      style={{ background: 'transparent', border: 'none', color: 'var(--brand-primary)', cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit', fontWeight: 600, padding: '6px 0', margin: '-6px 0' }}
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'} name="current-password" value={password} onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••" style={{ ...inp, paddingRight: '48px' }}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      style={{ position: 'absolute', right: '2px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', width: '44px', height: '44px', padding: 0, cursor: 'pointer', color: showPassword ? 'var(--brand-primary)' : '#9CA3AF', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 0 }}
                    >
                      {/* One `eye` glyph, state carried by color: the brand icon set
                          has no eye-off, and adding one is a change to the shared
                          54-icon system rather than to this screen. aria-pressed
                          carries the state for anyone not seeing the color. */}
                      <Icon name="eye" size={16} />
                    </button>
                  </div>
                </div>

                <button
                  type="submit" disabled={loading}
                  style={pkPrimary
                    ? { width: '100%', padding: '12px', background: 'white', color: '#374151', border: '1.5px solid #E5E7EB', borderRadius: '4px', fontSize: '13px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'background 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }
                    : { width: '100%', padding: '13px', background: loading ? '#F3F4F6' : 'var(--brand-primary)', color: loading ? '#9CA3AF' : 'white', border: 'none', borderRadius: '4px', fontSize: '14px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'background 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                >
                  {loading ? (
                    <>
                      <div style={{ width: '16px', height: '16px', border: '2px solid #D1D5DB', borderTopColor: '#9CA3AF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      Signing in…
                    </>
                  ) : 'Sign in'}
                </button>
              </form>
            </>
          ) : showForgot ? (
            <>
              {forgotSent ? (
                <>
                  <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#374151', lineHeight: 1.6 }}>
                    If an account exists for <strong>{forgotEmail}</strong>, we just sent it a reset link.
                    Check your inbox. The link expires in 1 hour.
                  </p>
                  <p style={{ margin: '0 0 24px', fontSize: '12px', color: '#9CA3AF', lineHeight: 1.6 }}>
                    Don't see it? Check spam, or try again in a minute.
                  </p>
                  <button
                    onClick={closeForgot}
                    style={{ width: '100%', padding: '13px', background: 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: '4px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    Back to sign in
                  </button>
                </>
              ) : (
                <>
                  <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6B7280', lineHeight: 1.5 }}>
                    Enter your email and we'll send you a link to set a new password.
                  </p>
                  {forgotError && (
                    <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '4px', padding: '10px 12px', marginBottom: '16px', color: '#DC2626', fontSize: '13px' }}>
                      {forgotError}
                    </div>
                  )}
                  <form onSubmit={e => { e.preventDefault(); if (!forgotSubmitting) handleForgotSubmit(); }}>
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>Email</label>
                      <input
                        type="email"
                        name="username"
                        autoComplete="username"
                        value={forgotEmail}
                        onChange={e => setForgotEmail(e.target.value)}
                        placeholder="you@example.com"
                        style={inp}
                        autoFocus
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={forgotSubmitting || !forgotEmail}
                      style={{ width: '100%', padding: '13px', background: forgotSubmitting || !forgotEmail ? '#F3F4F6' : 'var(--brand-primary)', color: forgotSubmitting || !forgotEmail ? '#9CA3AF' : 'white', border: 'none', borderRadius: '4px', fontSize: '14px', fontWeight: 700, cursor: forgotSubmitting || !forgotEmail ? 'not-allowed' : 'pointer', fontFamily: 'inherit', marginBottom: '12px' }}
                    >
                      {forgotSubmitting ? 'Sending…' : 'Send reset link'}
                    </button>
                    <button
                      type="button"
                      onClick={closeForgot}
                      style={{ width: '100%', background: 'transparent', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit', padding: '4px 0' }}
                    >
                      Back to sign in
                    </button>
                  </form>
                </>
              )}
            </>
          ) : (
            <>
              <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6B7280', lineHeight: 1.5 }}>
                {isPasskey
                  ? (deviceHasPasskey()
                      ? 'Use your passkey — Face ID, fingerprint, or a security key — to finish signing in.'
                      : "This account's passkey isn't set up on this device. Tap below and, if prompted, scan the QR code with the phone or computer where you originally set it up.")
                  : isSms
                    ? <>We texted a 6-digit code to <strong>••••{phoneLast4 || '••••'}</strong>. Enter it below.</>
                    : isEmail
                      ? 'We emailed a 6-digit code to your account address. Enter it below.'
                      : isRecovery
                        ? 'Enter one of your recovery codes.'
                        : 'Enter the 6-digit code from your authenticator app.'}
              </p>

              {/* Passkey (or any single-factor account) with no working local/
                  cross-device credential on this device is no longer a dead
                  end — "Use a different option" below always includes "Email
                  code" regardless of what's actually enrolled (see otherOptions
                  and mfa.py's mfa_email_send: every account has a verified
                  email, so this never requires prior setup). A prior version
                  of this screen pointed a passkey-only client at "contact your
                  advisor" here because there was genuinely nothing else to
                  try — that's no longer true, so this note is gone. */}

              {/* Trust device — rendered ABOVE the code input so the
                  user can tick it BEFORE typing the 6-digit code (which
                  auto-submits). Not offered for recovery codes (those are
                  emergency bypasses). */}
              {!isRecovery && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px', background: '#F9FAFB', border: '1px solid #F3F4F6', borderRadius: '4px', marginBottom: '16px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={trustDevice}
                    onChange={e => setTrustDevice(e.target.checked)}
                    style={{ marginTop: '3px', flexShrink: 0 }}
                  />
                  <div style={{ fontSize: '12px', lineHeight: 1.5, color: '#374151' }}>
                    <span style={{ fontWeight: 600, color: '#111827' }}>Remember this device for 30 days</span>
                    <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6B7280' }}>
                      Skip the code on next login from this browser. <strong>Only on devices you trust.</strong> Anyone with access to this browser will be able to log in with just your password.
                    </p>
                  </div>
                </label>
              )}

              {isPasskey ? (
                <button
                  onClick={() => startPasskey()} disabled={loading}
                  style={{ width: '100%', padding: '13px', background: loading ? '#F3F4F6' : 'var(--brand-primary)', color: loading ? '#9CA3AF' : 'white', border: 'none', borderRadius: '4px', fontSize: '14px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}
                >
                  {loading ? (
                    <>
                      <div style={{ width: '16px', height: '16px', border: '2px solid #D1D5DB', borderTopColor: '#9CA3AF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      Waiting for passkey…
                    </>
                  ) : (
                    <>
                      <Icon name="lock" size={16} />
                      Use passkey
                    </>
                  )}
                </button>
              ) : (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                      {isRecovery ? 'Recovery code' : 'Verification code'}
                    </label>
                    <input
                      type="text"
                      value={mfaCode}
                      onChange={e => setMfaCode(e.target.value)}
                      placeholder={isRecovery ? 'xxxx-xxxx' : '000000'}
                      inputMode={isRecovery ? 'text' : 'numeric'}
                      autoComplete="one-time-code"
                      maxLength={isRecovery ? 9 : 6}
                      autoFocus
                      style={{ ...inp, textAlign: 'center', fontSize: '20px', letterSpacing: isRecovery ? '0.04em' : '0.4em', fontFamily: 'ui-monospace, "SF Mono", monospace', fontWeight: 600 }}
                      onKeyDown={e => e.key === 'Enter' && handleMfaVerify()}
                    />
                  </div>

                  <button
                    onClick={handleMfaVerify} disabled={loading}
                    style={{ width: '100%', padding: '13px', background: loading ? '#F3F4F6' : 'var(--brand-primary)', color: loading ? '#9CA3AF' : 'white', border: 'none', borderRadius: '4px', fontSize: '14px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'background 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}
                  >
                    {loading ? (
                      <>
                        <div style={{ width: '16px', height: '16px', border: '2px solid #D1D5DB', borderTopColor: '#9CA3AF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        Verifying…
                      </>
                    ) : 'Verify'}
                  </button>

                </>
              )}

              {/* ONE contextual alternate, not a stack of every escape hatch.
                  A client who reported "there was no email code option" was
                  looking at it: it existed, collapsed behind a 12px link, which
                  is not discoverable when you're locked out and frustrated. The
                  fix is to surface the single best next thing for THIS user's
                  situation, since surfacing all of them reproduces the same
                  problem in the opposite direction — six affordances read as
                  noise and none of them reads as the answer.

                  Priority: a synced passkey this browser hasn't flagged yet is
                  the one-tap win, so it wins when the account has one. Otherwise
                  the universal zero-setup email fallback (mfa_email_send). The
                  full chooser stays below for everything else. */}
              {contextualAlt && (
                <button
                  onClick={() => selectMethod(contextualAlt)}
                  disabled={loading}
                  style={{ width: '100%', padding: '11px', background: 'white', color: '#374151', border: '1.5px solid #E5E7EB', borderRadius: '4px', fontSize: '13px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}
                >
                  <Icon name={contextualAlt === 'passkey' ? 'lock' : 'mail'} size={15} />
                  {contextualAlt === 'passkey' ? 'Try Face ID / Touch ID instead' : 'Email me a code instead'}
                </button>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                  {(isSms || isEmail) ? (
                    <button
                      onClick={() => mfaToken && (isSms ? sendLoginSms(mfaToken) : sendLoginEmailCode(mfaToken))}
                      disabled={resendIn > 0}
                      style={{ background: 'transparent', border: 'none', color: 'var(--brand-primary)', cursor: resendIn > 0 ? 'default' : 'pointer', fontSize: '12px', fontFamily: 'inherit', fontWeight: 600, padding: 0, opacity: resendIn > 0 ? 0.5 : 1 }}
                    >
                      {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
                    </button>
                  ) : <span />}
                  <button
                    onClick={handleCancelMfa}
                    style={{ background: 'transparent', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit', padding: 0 }}
                  >
                    Cancel
                  </button>
                </div>

                {/* Chooser is now ALWAYS available (not gated on
                    mfaMethods.length > 1) — "Email code" is always one of
                    otherOptions regardless of what's actually enrolled, since
                    it's the universal zero-setup fallback (mfa_email_send).
                    Lets the user pick a different way to verify (authenticator
                    lost → text/email a code, or recovery). */}
                {otherOptions.length > 0 && (
                  !showOtherOptions ? (
                    <button
                      onClick={() => setShowOtherOptions(true)}
                      style={{ background: 'transparent', border: 'none', color: 'var(--brand-primary)', cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit', fontWeight: 600, padding: '4px 0' }}
                    >
                      Use a different option
                    </button>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid #F3F4F6', paddingTop: '10px' }}>
                      <p style={{ margin: 0, fontSize: '11px', color: '#9CA3AF', fontWeight: 600 }}>Verify another way</p>
                      {otherOptions.map(m => (
                        <button
                          key={m}
                          onClick={() => selectMethod(m as 'sms' | 'totp' | 'recovery' | 'email')}
                          style={{ textAlign: 'left', background: 'white', border: '1px solid #E5E7EB', borderRadius: '4px', padding: '9px 12px', fontSize: '13px', fontWeight: 600, color: '#374151', cursor: 'pointer', fontFamily: 'inherit' }}
                        >
                          {METHOD_LABEL[m] || m}
                        </button>
                      ))}
                    </div>
                  )
                )}
              </div>
            </>
          )}

          <AddToHomeScreenNudge />
        </div>

        <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '12px', color: '#9CA3AF' }}>
          {FIRM.short_name} · Powered by Sourdough
        </p>
        <p style={{ textAlign: 'center', marginTop: '6px', fontSize: '11px', color: '#9CA3AF' }}>
          <a href="/privacy" style={{ color: '#9CA3AF', textDecoration: 'none' }}>Privacy Policy</a>
        </p>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
